import { Client, FishingGuide, FishingTrip, User, SiteConfig, FinancialRecord, MarketingPage } from '../types';
import { db as firestore, storage, auth } from './firebase';
import { STANDARD_SERVICES } from '../constants';

// Service Modules
import * as coreService from './modules/coreService';
import * as authService from './modules/authService';
import * as clientService from './modules/clientService';
import * as tripService from './modules/tripService';
import * as financialService from './modules/financialService';
import * as marketingService from './modules/marketingService';
import * as configService from './modules/configService';
import * as guideService from './modules/guideService';
import * as reportingService from './modules/reportingService';

class FirebaseDB {
  private clients: Client[] = [];
  private guides: FishingGuide[] = [];
  private trips: FishingTrip[] = [];
  private users: User[] = []; 
  private financialRecords: FinancialRecord[] = [];
  private marketingPages: MarketingPage[] = []; 
  
  private currentUser: User | null = null;
  private siteConfig: SiteConfig | null = null;
  
  private initialized = false;
  private isConnected = false; 
  private lastError: string | null = null;
  private defaultConfig: any = {}; 

  async init() {
    if (this.initialized) return;
    try {
      const storedUser = localStorage.getItem('sos_user');
      if (storedUser) {
          this.currentUser = JSON.parse(storedUser);
          await this.loadData(); // Full Load for Admin/Staff
      } else {
          await this.loadPublicData(); // Safe Load for Guests
      }
      this.initialized = true;
    } catch (error: any) {
      const { isConnected, lastError } = coreService.handleLoadError(error);
      this.isConnected = isConnected;
      this.lastError = lastError;
      this.initialized = true;
    }
  }

  private async loadPublicData() {
      // Only fetch Config and Marketing Pages for public site
      try {
          const [config, marketingPages] = await Promise.all([
             configService.fetchConfig(firestore, this.defaultConfig),
             marketingService.fetchMarketingPages(firestore)
          ]);
          this.siteConfig = config;
          this.marketingPages = marketingPages;
          this.isConnected = true; 
      } catch (err) {
          console.warn("Error loading public data", err);
      }
  }

  private async loadData() {
    try {
      // Parallel Fetching using Services
      const [
        config,
        users,
        clients,
        guides,
        trips,
        financialRecords,
        marketingPages
      ] = await Promise.all([
        configService.fetchConfig(firestore, this.defaultConfig),
        authService.fetchUsers(firestore),
        clientService.fetchClients(firestore),
        guideService.fetchGuides(firestore),
        tripService.fetchTrips(firestore),
        financialService.fetchFinancialRecords(firestore),
        marketingService.fetchMarketingPages(firestore)
      ]);

      this.siteConfig = config;
      this.users = users;
      this.clients = clients;
      this.guides = guides;
      this.trips = trips;
      this.financialRecords = financialRecords;
      this.marketingPages = marketingPages;
      
      this.isConnected = true; 
    } catch (dataErr: any) { 
        const { isConnected, lastError } = coreService.handleLoadError(dataErr);
        this.isConnected = isConnected;
        this.lastError = lastError;
    }
  }

  async reconnect() { this.initialized = false; await this.init(); return this.isConnected; }
  getDbSetupError() { return false; }
  getConnectionStatus() { return this.isConnected; }
  getLastError() { return this.lastError; }

  // Facade Methods for Cache Access
  getClients() { return this.clients; }
  getGuides() { return this.guides; }
  getTrips() { return this.trips; }
  getSystemUsers() { return this.users; }
  getMarketingPages() { return this.marketingPages; }
  getCurrentUser() { return this.currentUser; }
  getSiteConfig() { return this.siteConfig || this.defaultConfig; }
  getClient(id: string) { return this.clients.find(c => c.id === id); }
  getTrip(id: string) { return this.trips.find(t => t.id === id); }
  
  async getTripByCode(code: string) { 
    // 1. Try local cache first (fast)
    const cached = this.trips.find(t => t.fishingCode === code);
    if (cached) return cached;

    // 2. Fallback to Firestore (ensure freshness)
    try {
      if (!this.isConnected) await this.init();
      const freshTrip = await tripService.fetchTripByCode(firestore, code);
      if (freshTrip) {
        // Update local cache
        const exists = this.trips.find(t => t.id === freshTrip.id);
        if (!exists) this.trips.push(freshTrip);
        else this.trips = this.trips.map(t => t.id === freshTrip.id ? freshTrip : t);
        
        return freshTrip;
      }
    } catch (err) {
      console.error("Error fetching trip by code:", err);
    }
    return undefined;
  }
  
  getStats() {
    // Delegated to Reporting Service
    return reportingService.getStats(this.clients, this.trips);
  }

  // --- CLIENTS ---
  async addClient(client: Client) {
    this.clients.push(client);
    await clientService.addClient(firestore, client);
  }

  async updateClient(client: Client) {
    this.clients = this.clients.map(c => c.id === client.id ? client : c);
    await clientService.updateClient(firestore, client);
  }

  async removeClient(id: string) {
    this.clients = this.clients.filter(c => c.id !== id);
    await clientService.removeClient(firestore, id);
  }

  // --- TRIPS ---
  async addTrip(trip: FishingTrip) {
    this.trips.push(trip);
    await tripService.addTrip(firestore, trip);
  }

  async updateTrip(trip: FishingTrip) {
    this.trips = this.trips.map(t => t.id === trip.id ? trip : t);
    await tripService.updateTrip(firestore, trip);
  }

  async removeTrip(id: string) {
    this.trips = this.trips.filter(t => t.id !== id);
    await tripService.removeTrip(firestore, id);
  }

  // --- FINANCIAL ---
  async getFinancialStatement(monthStr: string) {
    return financialService.calculateFinancialStatement(this.financialRecords, this.trips, monthStr);
  }

  async addFinancialRecord(record: FinancialRecord) {
    this.financialRecords.push(record);
    await financialService.addFinancialRecord(firestore, record);
  }

  async updateFinancialRecord(record: FinancialRecord) {
    this.financialRecords = this.financialRecords.map(r => r.id === record.id ? record : r);
    await financialService.updateFinancialRecord(firestore, record);
  }

  async removeFinancialRecord(id: string) {
    this.financialRecords = this.financialRecords.filter(r => r.id !== id);
    await financialService.removeFinancialRecord(firestore, id);
  }

  // --- MARKETING & BUILDER ---
  getBuilderPage(id: string): MarketingPage | undefined {
      return marketingService.getBuilderPage(id, this.marketingPages, this.siteConfig);
  }

  async addMarketingPage(p: MarketingPage) { 
    // Optimization: Add to local cache immediately
    this.marketingPages.push(p);
    await marketingService.addMarketingPage(firestore, this.marketingPages, p);
  }
  
  async updateMarketingPage(p: MarketingPage) { 
     const updatedPage = await marketingService.updateMarketingPage(firestore, storage, this.marketingPages, p);
     this.marketingPages = this.marketingPages.map(x => x.id === p.id ? updatedPage : x);
  }

  async removeMarketingPage(id: string) {
    this.marketingPages = this.marketingPages.filter(p => p.id !== id);
    await marketingService.removeMarketingPage(firestore, id);
  }

  async saveBuilderPage(page: MarketingPage, type: 'marketing' | 'custom') {
     if (type === 'custom') {
        const currentPages = this.siteConfig?.customPages || [];
        const updatedPages = currentPages.map((p: any) => {
           if (p.id === page.id) {
              return { ...p, components: page.components }; 
           }
           return p;
        });
        
        const newConfig = { ...this.siteConfig!, customPages: updatedPages };
        await this.updateSiteConfig(newConfig);
     } else {
        await this.updateMarketingPage(page);
     }
  }

  // --- CONFIG ---
  async updateSiteConfig(newConfig: SiteConfig) {
    const savedConfig = await configService.updateSiteConfig(firestore, storage, newConfig);
    this.siteConfig = savedConfig;
  }

  // --- AUTH ---
  async loginWithEmailPassword(email: string, pass: string) {
    const user = await authService.loginWithEmailPassword(auth, this.users, email, pass);
    this.currentUser = user;
    if (user) {
        await this.loadData(); // Load full data for admin
    }
    return user;
  }

  async logout() {
    await authService.logout(auth);
    this.currentUser = null;
  }

  // --- USERS & GUIDES ---
  async addSystemUser(user: User) {
    this.users.push(user);
    await authService.addSystemUser(firestore, user);
  }

  async updateSystemUser(user: User) {
    this.users = this.users.map(u => u.uid === user.uid ? user : u);
    await authService.updateSystemUser(firestore, user);
  }

  async removeSystemUser(uid: string) {
    this.users = this.users.filter(u => u.uid !== uid);
    await authService.removeSystemUser(firestore, uid);
  }

  async addGuide(guide: FishingGuide) {
    this.guides.push(guide);
    await guideService.addGuide(firestore, guide);
  }

  async updateGuide(guide: FishingGuide) {
    this.guides = this.guides.map(g => g.id === guide.id ? guide : g);
    await guideService.updateGuide(firestore, guide);
  }

  async removeGuide(id: string) {
    this.guides = this.guides.filter(g => g.id !== id);
    await guideService.removeGuide(firestore, id);
  }
}

export const db = new FirebaseDB();